import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import {PrimaryCell, ResponsiveTable, TableColumnType} from '@hconnect/uikit'
import {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {makeStyles} from 'tss-react/mui'

import {useResponsiveBreakpoints} from '../../../../Molecules/Responsive.utils'
import {useFeaturesState} from '../../../../Organisms/Features'
import {usePermissions} from '../../../../Permissions'
import {Delivery} from '../../../Finance/Invoices/Delivery.types'
import {DeliveryCardList} from '../../components'
import {
  DeliveryArrivalTime,
  DeliveryLeftAt,
  DeliveryStatus,
  DeliveryTicketNumber,
  DeliveryVehicle
} from '../../components/DeliveryFields'
import {QuantityUomToSymbolComponent} from '../../Order.components'
import {Order} from '../../Order.types'

import {DeliveriesServicesSwitch, TableButtonSwitch} from './DeliveriesServicesSwitch'
import {filterDeliveriesIfService, getDeliveryToggles} from './utils'

const useStyles = makeStyles()(() => ({
  rowSelected: {
    backgroundColor: '#EBF3FC'
  }
}))

interface DeliveriesTableProps {
  order?: Order
  deliveries?: Delivery[]
  customerId: string
  analytics?: any
  setSelectedDelivery: (delivery: Delivery) => void
  isFetching: boolean
  selectedDeliveryId?: string
  deliveryIdsWithNotes?: Set<string>
}

export const DeliveriesTable: React.FC<DeliveriesTableProps> = ({
  order,
  deliveries,
  customerId,
  analytics,
  setSelectedDelivery,
  isFetching,
  selectedDeliveryId,
  deliveryIdsWithNotes = new Set<string>()
}) => {
  const {classes} = useStyles()
  const {t} = useTranslation()
  const features = useFeaturesState()
  const permissions = usePermissions()
  const {smallScreen} = useResponsiveBreakpoints()
  const [relevantDeliveries, setRelevantDeliveries] = useState<Delivery[]>([])
  const [{serviceDeliveries, materialDeliveries}, setDeliveries] = useState(() =>
    filterDeliveriesIfService(deliveries)
  )
  const [selectedSwitch, setSelectedSwitch] = useState<TableButtonSwitch>(
    TableButtonSwitch.deliveries || TableButtonSwitch.services
  )

  // Row highlight controlled by parent's selectedDeliveryId
  const rowOptions = useMemo(() => ({
    resolveClassName: (row: Delivery) =>
      selectedDeliveryId && row.deliveryId === selectedDeliveryId ? classes.rowSelected : ''
  }), [selectedDeliveryId, classes.rowSelected])

  useEffect(() => {
    setDeliveries(filterDeliveriesIfService(deliveries))
  }, [deliveries])

  useEffect(() => {
    if (serviceDeliveries.length && !materialDeliveries.length) {
      setSelectedSwitch(TableButtonSwitch.services)
    }
  }, [serviceDeliveries, materialDeliveries])

  useEffect(() => {
    if (!isFetching) {
      const areServicesSelected = selectedSwitch === TableButtonSwitch.services
      const relDeliveries = areServicesSelected ? serviceDeliveries : materialDeliveries
      setRelevantDeliveries(relDeliveries)
    }
  }, [selectedSwitch, isFetching, deliveries, serviceDeliveries, materialDeliveries])

  if (!order) return null

  const toggles = getDeliveryToggles(features, permissions, order)
  const rowsPool: {[key: string]: TableColumnType<Delivery>} = {
    ticketNumber: {
      field: 'ticket-number',
      headerName: t('order.expand.deliveryNumber'),
      renderCell: (delivery: Delivery) => (
        <DeliveryTicketNumber
          delivery={delivery}
          toggles={toggles}
          customerId={customerId}
          analytics={analytics}
        />
      )
    },
    leftPlantAt: {
      field: 'left-plant-at',
      headerName: toggles.isDeliveryLoadFinished
        ? t('order.expand.leftAt')
        : t('order.expand.shippingDate'),

      renderCell: (delivery: Delivery) => (
        <DeliveryLeftAt
          delivery={delivery}
          isDeliveryLoadFinished={toggles.isDeliveryLoadFinished}
          isSloCountry={toggles.isSloCountry}
          businessLine={order.businessLine}
        />
      )
    },
    arrival: {
      field: 'arrival',
      headerName: t('order.expand.arrival'),
      renderCell: (delivery: Delivery) => <DeliveryArrivalTime delivery={delivery} />
    },
    quantity: {
      field: 'quantity',
      headerName: toggles.isRMC ? t('order.expand.quantity') : t('order.expand.qtyLoaded'),
      renderCell: (delivery: Delivery) => (
        <>
          {delivery.loadQuantity}{' '}
          <QuantityUomToSymbolComponent uom={delivery.loadQuantityUom || ''} />
        </>
      )
    },
    plantName: {
      field: 'plant-name',
      headerName: t('order.orderDetails.plantName'),
      renderCell: (delivery: Delivery) => delivery.plantName || '-'
    },
    vehicle: {
      field: 'vehicle',
      headerName: t(`order.expand.modeoftransport.${order.modeOfTransport || 'others'}`),
      renderCell: (delivery: Delivery) => (
        <DeliveryVehicle
          delivery={delivery}
          customerId={customerId}
          toggles={toggles}
          analytics={analytics}
        />
      )
    },
    annotation: {
      field: 'annotation',
      headerName: t('order.orderDetails.deliveryAnnotations'),
      renderCell: (delivery: Delivery) => (
        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          {deliveryIdsWithNotes.has(delivery.deliveryId) && (
            <InsertDriveFileOutlinedIcon sx={{fontSize: 16, color: 'text.primary'}} />
          )}
        </div>
      )
    },
    status: {
      field: 'status',
      headerName: t('order.orderDetails.statusLabel'),
      renderCell: (delivery: Delivery) => (
        <DeliveryStatus delivery={delivery} toggles={toggles} customerId={customerId} />
      )
    },
    relatedDelivery: {
      field: 'related-delivery',
      noColumnSelection: true,
      headerName: t('order.expand.relatedDelivery'),
      renderCell: (delivery: Delivery) =>
        delivery.relatedDelivery ? (
          <DeliveryTicketNumber
            delivery={delivery.relatedDelivery}
            toggles={toggles}
            customerId={customerId}
            analytics={analytics}
          />
        ) : (
          <DeliveryTicketNumber
            delivery={delivery}
            toggles={toggles}
            customerId={customerId}
            analytics={analytics}
            useDeliveryNumber
          />
        )
    },
    description: {
      field: 'description',
      headerName: t('order.expand.serviceDescription'),
      noColumnSelection: true,
      renderCell: (delivery: Delivery) => (
        <PrimaryCell>{delivery.materialDescription || '-'}</PrimaryCell>
      )
    }
  }
  const materialColumns = [
    rowsPool.ticketNumber,
    !toggles.isRMC ? rowsPool.leftPlantAt : rowsPool.arrival,
    rowsPool.quantity,
    rowsPool.plantName,
    rowsPool.vehicle,
    rowsPool.annotation,
    rowsPool.status
  ]

  const serviceColumns = [
    rowsPool.relatedDelivery,
    rowsPool.description,
    rowsPool.quantity,
    rowsPool.plantName,
    rowsPool.status
  ]

  if (!isFetching && !deliveries?.length) return null

  return (
    <div data-test-id="deliveries-table">
      <DeliveriesServicesSwitch
        t={t}
        disableServicesSwitch={serviceDeliveries.length === 0}
        disableDeliveriesSwitch={materialDeliveries.length === 0}
        setSelectedSwitch={setSelectedSwitch}
      />
      {smallScreen ? (
        <DeliveryCardList
          customerId={customerId}
          deliveries={relevantDeliveries}
          order={order}
          toggles={toggles}
          viewAll
          analytics={analytics}
          loading={isFetching}
          onCardClick={(delivery) => {
            if (!delivery.isService) {
              setSelectedDelivery(delivery)
            }
          }}
        />
      ) : (
        <ResponsiveTable
          columns={selectedSwitch === TableButtonSwitch.services ? serviceColumns : materialColumns}
          rows={relevantDeliveries}
          onRowClick={(row) =>
            // TODO: remove 'au' check when 'isService' property is fixed for AU deliveries
            (!row.isService || row.deliveryId.split('.')[0] === 'au') && setSelectedDelivery(row)
          }
          onSort={() => {}}
          loading={isFetching}
          selectionEnabled={false}
          keyField="deliveryId"
          rowDetailsAvailable
          rowOptions={rowOptions}
        />
      )}
    </div>
  )
}
